<script setup>
/**
 * ItemDialogDetails Component
 * Premium item details layout
 * Supports promo pricing display; discount-ladder tiers are tappable
 * quantity shortcuts (tap a tier -> that quantity goes in the cart)
 */
import { computed, ref, toRef } from 'vue'
import { useCartStore } from '../../../../app/store/cart.js'
import { useItemPricing } from '../../../../core/composables/useItemPricing.js'
import { hapticTap } from '../../../../core/utils/haptics.js'
import {
  formatPrice,
  safeText,
  formatBoxSizeText,
  requiresBoxMultiples,
} from './itemDialogUtils.js'

const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
  departmentName: {
    type: String,
    default: null,
  },
  subDepartmentName: {
    type: String,
    default: null,
  },
  // Promo info
  promoInfo: {
    type: Object,
    default: null,
    // Expected shape: { isPromo: boolean, unitPrice: number, step: { description } | null, promo: { label, first_order } | null }
  },
  // Amount discount ladder (with base price as first tier)
  discountLadder: {
    type: Array,
    default: null,
    // Expected shape: [{ minQty, discountPercent, unitPriceAfterDiscount, isActive, isBase? }]
  },
  // Stock
  stockLoading: {
    type: Boolean,
    default: false,
  },
  stockData: {
    type: Array,
    default: null,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  showPrices: {
    type: Boolean,
    default: true,
  },
})

// Computed values
const hasItem = computed(() => !!props.item)
const itemName = computed(() => props.item?.name || 'פריט לא ידוע')
const itemMakat = computed(() => props.item?.makat || '—')
const itemPrice = computed(() => formatPrice(props.item?.price))
const hasPrice = computed(() => props.item?.price != null && props.item?.price !== '')
const itemBarcode = computed(() => safeText(props.item?.barcode))
const boxSizeText = computed(() => formatBoxSizeText(props.item))
const isOnlyBox = computed(() => requiresBoxMultiples(props.item))
const itemOrigin = computed(() => safeText(props.item?.origin))
const hasReturnsWarning = computed(() => props.item?.returns === '1')
const itemKosherType = computed(() => safeText(props.item?.kosher_type))
const hasRabanut = computed(
  () => props.item?.kosher_rabanut === '1' || props.item?.kosher_rabanut === '2'
)
const canOrderByUnits = computed(() => props.item?.onlyBox === 1 || props.item?.onlyBox === '1')

// Promo pricing
const hasPromo = computed(() => props.promoInfo?.isPromo === true)
const promoUnitPrice = computed(() => {
  if (!hasPromo.value || !props.promoInfo?.unitPrice) return null
  return props.promoInfo.unitPrice.toFixed(2)
})
const promoStepDescription = computed(() => {
  return props.promoInfo?.step?.description || null
})
const promoLabel = computed(() => {
  if (!hasPromo.value || !props.promoInfo?.promo) return null
  if (props.promoInfo.promo.first_order) {
    return 'מבצע הזמנה ראשונה'
  }
  if (props.promoInfo.promo.weeklyBenefit) {
    return props.promoInfo.promo.label || 'הטבה שבועית'
  }
  return props.promoInfo.promo.label || 'מבצע מדרגות'
})

// Amount discount ladder
const hasDiscountLadder = computed(() => {
  return props.discountLadder && props.discountLadder.length > 0
})

// Tappable tiers: shared pricing source of truth for cart/group quantities
const cartStore = useCartStore()
const {
  cartQty: pricingCartQty,
  stepSize: pricingStepSize,
  isOnlyBox: pricingOnlyBox,
  boxSize: pricingBoxSize,
  discountTotalQty,
  hasValidPrice: pricingHasValidPrice,
} = useItemPricing(toRef(props, 'item'))

/**
 * Tap a discount tier -> put exactly enough of THIS item in the cart to reach
 * the tier (the tier qty is cumulative across the paritMerakez group, so
 * quantities from sibling items count toward it). Never reduces an existing
 * quantity; box-only items round up to a full box.
 */
function selectDiscountTier(tier) {
  if (tier.isBase) return
  const makat = props.item?.makat
  if (!makat || !pricingHasValidPrice.value) return

  const otherItemsQty = Math.max(0, discountTotalQty.value - pricingCartQty.value)
  let target = Math.max(tier.minQty - otherItemsQty, pricingStepSize.value)
  if (pricingOnlyBox.value && pricingBoxSize.value > 0) {
    target = Math.ceil(target / pricingBoxSize.value) * pricingBoxSize.value
  }
  if (pricingCartQty.value >= target) return

  const result =
    pricingCartQty.value === 0 ? cartStore.addItem(makat, target) : cartStore.setQty(makat, target)
  if (result?.ok) hapticTap()
}

// Stock
const mainStock = computed(() => {
  if (!props.stockData) return null
  return props.stockData.find((w) => w.whName === 'Main')
})
const hasMainStock = computed(() => mainStock.value && mainStock.value.stock > 0)
const stockDisplay = computed(() => {
  if (props.stockLoading) return null
  if (!hasMainStock.value) return 'אין נתונים'
  return `${mainStock.value.stock} יחידות`
})
const adminWarehouses = computed(() => {
  if (!props.stockData) return []
  return props.stockData.filter((w) => w.stock > 0)
})
const showStockModal = ref(false)

// Location text
const locationText = computed(() => {
  const parts = []
  if (props.departmentName) parts.push(props.departmentName)
  if (props.subDepartmentName) parts.push(props.subDepartmentName)
  return parts.length > 0 ? parts.join(' / ') : null
})

// Packaging factors (PARTPACK_SUBFORM). Only 'K' (carton/box) and 'P' (pallet) are
// shown — other codes like 'PP' (purchasing pallet) are intentionally excluded.
const showPackModal = ref(false)
const packs = computed(() =>
  Array.isArray(props.item?.PARTPACK_SUBFORM) ? props.item.PARTPACK_SUBFORM : []
)
// Units in a single box (the 'K' carton) — used to express pallets in whole boxes.
const unitsPerBox = computed(() => {
  const box = packs.value.find((p) => p.PACKCODE === 'K')
  const qty = box ? Number(box.PACKQUANT) : null
  return qty && qty > 0 ? qty : null
})

function packLabel(p) {
  if (p.PACKCODE === 'K') return 'ארגז'
  if (p.PACKCODE === 'P') return 'משטח'
  return p.PACKNAME || p.PACKCODE || 'אריזה'
}

// One row per packaging unit, ordered by PACK level (box first). Only 'K' and 'P' are
// included. For the pallet we also derive how many boxes ('K') it holds (= units / unitsPerBox).
const packCombos = computed(() =>
  [...packs.value]
    .filter((p) => p.PACKCODE === 'K' || p.PACKCODE === 'P')
    .sort((a, b) => (Number(a.PACK) || 99) - (Number(b.PACK) || 99))
    .map((p) => {
      const qty = Number(p.PACKQUANT) || 0
      const isBox = p.PACKCODE === 'K'
      const boxes = !isBox && unitsPerBox.value ? qty / unitsPerBox.value : null
      return { code: p.PACKCODE, label: packLabel(p), qty, isBox, boxes }
    })
)

// Button shows only when there's a 'K' or 'P' pack to display.
const hasPacks = computed(() => packCombos.value.length > 0)

function formatCount(n) {
  if (n == null || Number.isNaN(n)) return '—'
  return Number.isInteger(n) ? String(n) : Number(n.toFixed(2)).toString()
}
</script>

<template>
  <div class="item-details">
    <!-- Price Section -->
    <div
      v-if="showPrices"
      class="price-section"
      :class="{ 'has-promo': hasPromo, 'has-discount-ladder': hasDiscountLadder && !hasPromo }"
    >
      <!-- Standard price display (no discount ladder) -->
      <template v-if="!hasDiscountLadder || hasPromo">
        <div class="price-label">מחיר ליחידה</div>
        <div class="price-row" :class="{ 'no-price': !hasPrice }">
          <!-- Original price (strike-through when promo) -->
          <span v-if="hasPromo" class="price-original">{{ itemPrice }} ₪</span>
          <!-- Current effective price -->
          <span class="price-value" :class="{ 'promo-price': hasPromo }">
            {{ hasPromo ? promoUnitPrice : itemPrice }}
          </span>
          <span v-if="hasPrice" class="price-currency">₪</span>
        </div>
        <!-- Promo badge and description -->
        <div v-if="hasPromo" class="promo-info">
          <span class="promo-badge">{{ promoLabel }}</span>
          <span v-if="promoStepDescription" class="promo-description">{{
            promoStepDescription
          }}</span>
        </div>
      </template>

      <!-- Discount Ladder Display -->
      <template v-else-if="hasDiscountLadder">
        <div class="discount-ladder-header">
          <svg
            class="discount-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <span>מחירון לפי כמות</span>
          <span class="ladder-tap-hint">לחצו לבחירת כמות</span>
        </div>
        <div class="discount-ladder-tiers">
          <button
            v-for="tier in discountLadder"
            :key="tier.minQty"
            type="button"
            class="discount-tier"
            :class="{ 'is-active': tier.isActive, 'is-base': tier.isBase }"
            :disabled="tier.isBase"
            @click="selectDiscountTier(tier)"
          >
            <span class="tier-qty">
              {{ tier.isBase ? 'מחיר בסיס' : `מ-${tier.minQty} יח'` }}
            </span>
            <span class="tier-price">₪{{ tier.unitPriceAfterDiscount }}</span>
            <span v-if="!tier.isBase && tier.discountPercent > 0" class="tier-discount">
              -{{ tier.discountPercent }}%
            </span>
          </button>
        </div>
      </template>
    </div>

    <!-- Guest: login to see prices -->
    <div v-else class="price-section guest-price-section">
      <span class="guest-price-label">התחבר לצפייה במחירים</span>
    </div>

    <!-- Info Grid -->
    <div class="info-grid">
      <!-- Barcode -->
      <div v-if="itemBarcode !== '—'" class="info-cell">
        <span class="info-label">
          <svg
            class="info-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M7 8v8M11 8v8M15 8v4M19 8v8" />
          </svg>
          ברקוד
        </span>
        <span class="info-value info-value-mono">{{ itemBarcode }}</span>
      </div>

      <!-- Stock -->
      <div class="info-cell">
        <span class="info-label">
          <svg
            class="info-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
          </svg>
          מלאי נוכחי
        </span>
        <span v-if="stockLoading" class="info-value">
          <span class="stock-spinner" />
        </span>
        <span
          v-else
          class="info-value"
          :class="{
            'info-value-success': hasMainStock,
            'info-value-muted': !hasMainStock,
            'stock-admin-clickable': isAdmin && adminWarehouses.length > 0,
          }"
          @click="isAdmin && adminWarehouses.length > 0 && (showStockModal = true)"
        >
          {{ stockDisplay }}
        </span>
      </div>

      <!-- Order by Units -->
      <div class="info-cell">
        <span class="info-label">
          <svg
            class="info-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          הזמנה ביחידות
        </span>
        <span
          class="info-value"
          :class="canOrderByUnits ? 'info-value-success' : 'info-value-error'"
        >
          {{ canOrderByUnits ? 'כן' : 'לא' }}
        </span>
      </div>

      <!-- Returns Warning -->
      <div v-if="hasReturnsWarning" class="info-cell info-cell-warning">
        <span class="info-label">
          <svg
            class="info-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          החזרות
        </span>
        <span class="info-value info-value-warning">ללא זכות החזרה</span>
      </div>

      <!-- Packaging factors -->
      <button
        v-if="hasPacks"
        type="button"
        class="info-cell info-cell-button"
        @click="showPackModal = true"
      >
        <span class="info-label">
          <svg
            class="info-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
            />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
          אריזה
        </span>
        <span class="info-value pack-link">גורמי אירוז</span>
      </button>
    </div>

    <!-- Admin Stock Modal -->
    <Teleport to="body">
      <Transition name="stock-modal">
        <div v-if="showStockModal" class="stock-modal-overlay" @click.self="showStockModal = false">
          <div class="stock-modal-card">
            <div class="stock-modal-header">
              <span>מלאי לפי מחסנים</span>
              <button class="stock-modal-close" @click="showStockModal = false">✕</button>
            </div>
            <div class="stock-modal-list">
              <div v-for="wh in adminWarehouses" :key="wh.whName" class="stock-modal-row">
                <span class="stock-wh-name">{{ wh.whName }}</span>
                <span class="stock-wh-balance">{{ wh.stock }} יחידות</span>
              </div>
              <div v-if="adminWarehouses.length === 0" class="stock-modal-empty">אין נתונים</div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Packaging Factors Modal -->
    <Teleport to="body">
      <Transition name="stock-modal">
        <div v-if="showPackModal" class="stock-modal-overlay" @click.self="showPackModal = false">
          <div class="stock-modal-card">
            <div class="stock-modal-header">
              <span>גורמי אירוז</span>
              <button class="stock-modal-close" @click="showPackModal = false">✕</button>
            </div>
            <div class="pack-modal-sub">{{ itemName }}</div>
            <div class="stock-modal-list">
              <div v-for="combo in packCombos" :key="combo.code" class="pack-combo-row">
                <span class="pack-combo-label">1 {{ combo.label }}</span>
                <span class="pack-combo-eq">
                  = {{ formatCount(combo.qty) }} יח'<template v-if="combo.boxes != null">
                    = {{ formatCount(combo.boxes) }} ארגזים</template
                  >
                </span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.item-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
}

/* Price Section */
.price-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(22, 163, 74, 0.08) 100%);
  border-radius: var(--border-radius-sm);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.price-section.guest-price-section {
  background: var(--bg-secondary, #f8fafc);
  border-color: var(--border-color, #e2e8f0);
  align-items: center;
  padding: var(--spacing-lg);
}

.guest-price-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary, #475569);
}

.price-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-row.no-price {
  color: var(--color-error);
}

.price-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: #16a34a;
}

.price-row.no-price .price-value {
  color: var(--color-error);
  font-size: var(--font-size-lg);
}

.price-currency {
  font-size: var(--font-size-base);
  color: #16a34a;
  font-weight: var(--font-weight-medium);
}

/* Promo price styling */
.price-section.has-promo {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%);
  border-color: rgba(239, 68, 68, 0.3);
}

.price-original {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  text-decoration: line-through;
  margin-inline-end: var(--spacing-sm);
}

.price-value.promo-price {
  color: #dc2626;
}

.promo-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
  flex-wrap: wrap;
}

.promo-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  border-radius: 4px;
  white-space: nowrap;
}

.promo-description {
  font-size: var(--font-size-sm);
  color: #b91c1c;
  font-weight: var(--font-weight-medium);
}

/* Discount Ladder Section */
.price-section.has-discount-ladder {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(5, 150, 105, 0.06) 100%);
  border-color: rgba(16, 185, 129, 0.25);
}

.discount-ladder-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #047857;
  margin-bottom: var(--spacing-sm);
}

.discount-icon {
  flex-shrink: 0;
  color: #059669;
}

.discount-ladder-tiers {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.ladder-tap-hint {
  margin-inline-start: auto;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
}

.discount-tier {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  text-align: start;
  cursor: pointer;
  transition: all 0.2s ease;
}

.discount-tier:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(5, 150, 105, 0.4);
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.12);
}

.discount-tier:not(:disabled):active {
  transform: scale(0.97);
}

.discount-tier:disabled {
  cursor: default;
}

.discount-tier.is-base {
  background: rgba(255, 255, 255, 0.8);
  border: 1px dashed rgba(5, 150, 105, 0.25);
}

.discount-tier.is-active {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.1) 100%);
  border: 1px solid rgba(5, 150, 105, 0.35);
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.1);
}

.tier-qty {
  flex: 1;
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.discount-tier.is-active .tier-qty {
  color: #047857;
  font-weight: var(--font-weight-semibold);
}

.tier-price {
  font-weight: var(--font-weight-bold);
  color: #059669;
  font-size: var(--font-size-base);
}

.discount-tier.is-active .tier-price {
  color: #047857;
  font-size: var(--font-size-lg);
}

.tier-discount {
  padding: 2px 8px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  border-radius: 4px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-sm);
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--border-color);
}

.info-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: var(--spacing-sm) var(--spacing-xs);
  text-align: center;
  min-width: 0;
}

.info-cell-warning {
  background: rgba(239, 68, 68, 0.05);
  border-radius: 4px;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  white-space: nowrap;
}

.info-icon {
  flex-shrink: 0;
  opacity: 0.5;
}

.info-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  word-break: break-word;
}

.info-value-mono {
  font-family: var(--font-family-mono, monospace);
  letter-spacing: 0.5px;
}

.info-value-muted {
  color: var(--text-muted);
}

.info-value-warning {
  color: var(--color-error);
}

.info-value-success {
  color: var(--color-success, #22c55e);
}

.info-value-error {
  color: var(--color-error, #ef4444);
}

/* Stock spinner */
.stock-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary, #3b82f6);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stock-admin-clickable {
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.stock-admin-clickable:hover {
  color: var(--color-primary, #3b82f6);
}

/* Stock modal */
.stock-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
}

.stock-modal-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-xl);
  width: 320px;
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.stock-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
}

.stock-modal-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 4px;
  line-height: 1;
}

.stock-modal-close:hover {
  color: var(--text-primary);
}

.stock-modal-list {
  padding: var(--spacing-sm) var(--spacing-lg);
  overflow-y: auto;
}

.stock-modal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color-light, rgba(0, 0, 0, 0.06));
}

.stock-modal-row:last-child {
  border-bottom: none;
}

.stock-wh-name {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.stock-wh-balance {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-success, #22c55e);
}

.stock-modal-empty {
  padding: var(--spacing-md) 0;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* Stock modal transitions */
.stock-modal-enter-active,
.stock-modal-leave-active {
  transition: opacity 0.2s ease;
}

.stock-modal-enter-active .stock-modal-card,
.stock-modal-leave-active .stock-modal-card {
  transition: transform 0.2s ease;
}

.stock-modal-enter-from,
.stock-modal-leave-to {
  opacity: 0;
}

.stock-modal-enter-from .stock-modal-card,
.stock-modal-leave-to .stock-modal-card {
  transform: scale(0.95);
}

/* Packaging factors trigger (button styled as an info cell) */
.info-cell-button {
  background: none;
  border: none;
  font: inherit;
  cursor: pointer;
}

.pack-link {
  color: var(--color-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.info-cell-button:hover .pack-link {
  color: var(--color-primary-hover);
}

/* Packaging factors modal */
.pack-modal-sub {
  padding: 0 var(--spacing-lg) var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
}

.pack-combo-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color-light, rgba(0, 0, 0, 0.06));
}

.pack-combo-row:last-child {
  border-bottom: none;
}

.pack-combo-label {
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  white-space: nowrap;
}

.pack-combo-eq {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  text-align: end;
}

/* Mobile & Tablet: compact spacing — price stays prominent, chrome shrinks */
@media (max-width: 1279px) {
  .item-details {
    padding: var(--spacing-sm) var(--spacing-md);
    gap: var(--spacing-sm);
  }

  .price-section {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .promo-info {
    margin-top: 0;
  }

  .info-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2px;
    padding-top: var(--spacing-xs);
  }

  .info-cell {
    padding: var(--spacing-xs) 2px;
    gap: 2px;
  }

  .price-value {
    font-size: var(--font-size-xl);
  }

  /* Discount ladder → horizontal chips, one glance instead of a stacked list */
  .discount-ladder-header {
    margin-bottom: var(--spacing-xs);
    font-size: var(--font-size-xs);
  }

  .discount-ladder-tiers {
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
  }

  .discount-tier {
    flex: 1 1 0;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 2px;
    padding: var(--spacing-xs);
  }

  .discount-tier .tier-qty {
    flex: none;
    font-size: 11px;
    white-space: nowrap;
  }

  .tier-price {
    font-size: var(--font-size-sm);
  }

  .discount-tier.is-active .tier-price {
    font-size: var(--font-size-base);
  }

  .tier-discount {
    padding: 1px 6px;
    font-size: 10px;
  }
}

/* Tablet & large phones: room for a wider info grid */
@media (min-width: 600px) and (max-width: 1279px) {
  .info-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
